import React, { useState, PropsWithChildren } from 'react'
import { useCombobox } from 'downshift'
import { FontAwesomeIcon as Icon } from '@fortawesome/react-fontawesome'
import { faCaretDown } from '@fortawesome/free-solid-svg-icons'
import { useTranslation } from 'react-i18next'

import { FilterInputStyles, InputContainer } from './FilterInput.styles'

const hightlightColor = '#F8F8F8'

type Props<T> = {
  type: T
  items: string[]
  placeholder?: string
  title?: string
  disabled?: boolean
  value?: string
  onInputChange?: (value: string) => void
  onItemSelect?: (item: { filter: string; type: T }) => void
  id?: string
  minLength?: number
  maxLength?: number
}

function FilterInput<T extends string>({
  items = [],
  value,
  onItemSelect,
  placeholder,
  title,
  type,
  disabled = false,
  onInputChange,
  id,
  minLength,
  maxLength,
}: PropsWithChildren<Props<T>>): React.ReactElement {
  const { t } = useTranslation()
  const [inputItems, setInputItems] = useState(items)
  const [innerInputValue, setInputValue] = useState('')

  const inputValue = typeof value !== 'undefined' ? value : innerInputValue

  // this effect logic is used when onInput change is not provided
  React.useEffect(() => {
    if (!onInputChange) {
      if (inputValue) {
        setInputItems(
          items.filter((item) => item.toLowerCase().includes(inputValue.trimStart().toLowerCase())),
        )
      } else {
        setInputItems(items)
      }
    }
  }, [items, inputValue, onInputChange])

  const autoCompleteItems = typeof onInputChange === 'function' ? items : inputItems

  const {
    isOpen,
    getToggleButtonProps,
    getMenuProps,
    getInputProps,
    getComboboxProps,
    getLabelProps,
    highlightedIndex,
    getItemProps,
    openMenu,
  } = useCombobox({
    items: autoCompleteItems,
    inputValue,
    // we don't want to have selected item for the input!
    // we only want to add selected items somewhere else
    selectedItem: null,
    onSelectedItemChange: (state) => {
      if (onItemSelect && state.inputValue) {
        onItemSelect({
          filter: state.inputValue,
          type,
        })
      }
      // we want input to be empty when selected
      setInputValue('')
      onInputChange && onInputChange('') // FIXME: This causes state update warnings
    },
  })

  const handleInputChange = (e: React.FormEvent<HTMLInputElement>) => {
    const formValue = e.currentTarget.value
    setInputValue(formValue.trimStart())
    if (typeof onInputChange === 'function') {
      onInputChange(formValue.trimStart())
    }
  }

  const showList = isOpen && autoCompleteItems.length > 0
  const menuProps = getMenuProps()

  return (
    <FilterInputStyles>
      {title && <label {...getLabelProps()}>{title.toUpperCase()}</label>}
      <InputContainer {...getComboboxProps()} listVisible={showList} disabled={disabled}>
        <input
          disabled={disabled}
          minLength={minLength}
          maxLength={maxLength}
          {...getInputProps({
            value: inputValue,
            onChange: handleInputChange,
            onFocus: openMenu,
            onKeyDown: (event) => {
              // allow form submit on enter if no items are highlighted (parent can wrap this in)
              if (event.key === 'Enter' && highlightedIndex < 0) {
                ;(event.nativeEvent as any).preventDownshiftDefault = true
              }
            },
          })}
          placeholder={placeholder || t('select_or_enter')}
          id={id}
        />
        <button
          {...getToggleButtonProps()}
          aria-label={t('toggle_dropdown')}
          type="button"
          disabled={disabled}
        >
          <Icon icon={faCaretDown} size="lg" />
        </button>
      </InputContainer>
      {showList && (
        <ul {...menuProps}>
          {autoCompleteItems.map((item, index) => (
            <li
              key={`${item}${index}`}
              style={
                highlightedIndex === index
                  ? {
                      backgroundColor: hightlightColor,
                    }
                  : {}
              }
              {...getItemProps({
                item,
                index,
              })}
            >
              {item}
            </li>
          ))}
        </ul>
      )}
    </FilterInputStyles>
  )
}

export default FilterInput
